import React, { useEffect, useRef, useState } from "react";
import { 
  Camera, 
  Users, 
  Wifi, 
  WifiOff, 
  AlertCircle,
  RefreshCw,
  Grid,
  List,
  User,
  UserCheck,
  Users as UsersIcon,
  PieChart
} from "lucide-react";

const WebSocketDataFeed = () => {
  const [cameraData, setCameraData] = useState({});
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [totalPersons, setTotalPersons] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // New state for demographic data
  const [demographics, setDemographics] = useState({
    ageGroups: {
      "20-30": 0,
      "30-40": 0,
      "40-50": 0,
      "50+": 0
    },
    gender: {
      male: 0,
      female: 0
    },
    totalUsed: 0 // Track which total was used for demographics
  });

  const eventSourceRef = useRef(null);
  const totalPersonsRef = useRef(0);
  const demographicTimerRef = useRef(null);
  const initialDataTimerRef = useRef(null);
  const isFirstDataReceivedRef = useRef(false);
  const isInitialDemographicsSetRef = useRef(false);

  // Function to generate realistic demographic data based on total persons
  const generateDemographics = (total) => {
    if (total === 0) {
      return {
        ageGroups: {
          "20-30": 0,
          "30-40": 0,
          "40-50": 0,
          "50+": 0
        },
        gender: {
          male: 0,
          female: 0
        },
        totalUsed: 0
      };
    }

    // Generate age distribution (20-30 and 30-40 have higher counts)
    const ageDistribution = {
      "20-30": Math.floor(total * (0.30 + Math.random() * 0.10)),
      "30-40": Math.floor(total * (0.30 + Math.random() * 0.10)),
      "40-50": Math.floor(total * (0.20 + Math.random() * 0.08)),
      "50+": Math.floor(total * (0.08 + Math.random() * 0.05))
    };

    // Ensure total matches by adjusting the largest group
    const currentTotal = Object.values(ageDistribution).reduce((a, b) => a + b, 0);
    const diff = total - currentTotal;
    if (diff !== 0) {
      const maxKey = Object.keys(ageDistribution).reduce((a, b) => 
        ageDistribution[a] > ageDistribution[b] ? a : b
      );
      ageDistribution[maxKey] += diff;
    }

    // Generate gender distribution (females only 2-5%)
    const femalePercentage = 0.02 + Math.random() * 0.03; // 2% to 5%
    const femaleCount = Math.floor(total * femalePercentage);
    const maleCount = total - femaleCount;

    return {
      ageGroups: ageDistribution,
      gender: {
        male: maleCount,
        female: femaleCount
      },
      totalUsed: total
    };
  };

  // Function to update demographics with current total
  const updateDemographics = () => {
    const currentTotal = totalPersonsRef.current;
    if (currentTotal > 0) {
      const newDemographics = generateDemographics(currentTotal);
      setDemographics(newDemographics);
    }
  };

  // Function to set initial demographics after 5 seconds
  const setInitialDemographics = () => {
    if (!isInitialDemographicsSetRef.current) {
      const currentTotal = totalPersonsRef.current;
      if (currentTotal > 0) {
        const newDemographics = generateDemographics(currentTotal);
        setDemographics(newDemographics);
        isInitialDemographicsSetRef.current = true;
      }
    }
  };

  // Function to create EventSource connection
  const createEventSource = () => {
    
    // const eventSource = new EventSource(
    //   "http://localhost:8000/api/vision/ws-data/stream"
    // );

    const eventSource = new EventSource(
      "https://cognivision-be.citriot.ai/api/vision/ws-data/stream"
    );


    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setConnected(true);
      setError(null);
      setIsRefreshing(false);
      
      // Reset initial data flags on new connection
      isFirstDataReceivedRef.current = false;
      isInitialDemographicsSetRef.current = false;
      
      // Clear any existing timer
      if (initialDataTimerRef.current) {
        clearTimeout(initialDataTimerRef.current);
        initialDataTimerRef.current = null;
      }
      
      // Start timer for initial demographics
      initialDataTimerRef.current = setTimeout(setInitialDemographics, 5000);
    };

    eventSource.onmessage = (event) => {

      try {
        const parsedData = JSON.parse(event.data);

        if (parsedData.type === "connected") {
          setConnected(true);
          setIsRefreshing(false);
          return;
        }

        if (parsedData.type === "disconnected") {
          setConnected(false);
          return;
        }

        if (parsedData.type === "error") {
          setError(parsedData.message);
          return;
        }

        if (
          parsedData.camera_name !== undefined &&
          parsedData.num_persons !== undefined
        ) {
          setCameraData((previousCameraData) => {
            const newData = {
              ...previousCameraData,
              [parsedData.camera_name]: parsedData.num_persons,
            };
            
            const total = Object.values(newData).reduce((sum, count) => sum + count, 0);
            setTotalPersons(total);
            totalPersonsRef.current = total;
            
            // Mark that we've received data
            if (!isFirstDataReceivedRef.current) {
              isFirstDataReceivedRef.current = true;
            }
            
            return newData;
          });
          
          setLastUpdate(new Date());
        }
      } catch (error) {
        console.error("[SSE] JSON parsing error:", error);
      }
    };

    eventSource.onerror = (error) => {
      console.error("[SSE] Connection error:", error);
      setConnected(false);
      setIsRefreshing(false);
    };

    return eventSource;
  };

  useEffect(() => {
    const eventSource = createEventSource();

    // Set up interval to update demographics every 30 seconds
    demographicTimerRef.current = setInterval(updateDemographics, 30000);

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (demographicTimerRef.current) {
        clearInterval(demographicTimerRef.current);
      }
      if (initialDataTimerRef.current) {
        clearTimeout(initialDataTimerRef.current);
      }
    };
  }, []);

  // Handle manual refresh
  const handleRefresh = () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setConnected(false);
    isFirstDataReceivedRef.current = false;
    isInitialDemographicsSetRef.current = false;
    
    // Clear existing timer
    if (initialDataTimerRef.current) {
      clearTimeout(initialDataTimerRef.current);
      initialDataTimerRef.current = null;
    }
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    
    setTimeout(() => {
      const newEventSource = createEventSource();
      eventSourceRef.current = newEventSource;
    }, 500);
  };

  const cameras = Object.entries(cameraData);
  const activeCameras = cameras.filter(([_, count]) => count > 0).length;

  const getStatusColor = (count) => {
    if (count === 0) return "bg-gray-100 text-gray-600";
    if (count <= 2) return "bg-green-100 text-green-700";
    if (count <= 5) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  const getStatusLabel = (count) => {
    if (count === 0) return "Empty";
    if (count <= 2) return "Low";
    if (count <= 5) return "Medium";
    return "High";
  };

  const getDotColor = (count) => {
    if (count === 0) return "bg-gray-300";
    if (count <= 2) return "bg-green-500";
    if (count <= 5) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Get max age group value for progress bar
  const maxAgeValue = Math.max(
    ...Object.values(demographics.ageGroups),
    1
  );

  // Use the stable total from demographics for percentage calculations
  const demographicTotal = demographics.totalUsed || totalPersons;

  return (
    <div className="space-y-6">
      {/* Header Stats - These update continuously */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Cameras</p>
              <p className="text-2xl font-bold text-slate-800">{cameras.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Camera className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Cameras With Events</p>
              <p className="text-2xl font-bold text-slate-800">{activeCameras}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <Wifi className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Persons</p>
              <p className="text-2xl font-bold text-slate-800">{totalPersons}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Connection Status</p>
              <div className="flex items-center gap-2 mt-1">
                <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'} ${!isRefreshing && 'animate-pulse'}`} />
                <p className={`text-lg font-bold ${connected ? 'text-green-600' : 'text-red-600'}`}>
                  {isRefreshing ? 'Reconnecting...' : connected ? 'Online' : 'Offline'}
                </p>
              </div>
            </div>
            <div className={`p-3 rounded-lg ${connected ? 'bg-green-100' : 'bg-red-100'}`}>
              {connected ? 
                <Wifi className="w-6 h-6 text-green-600" /> : 
                <WifiOff className="w-6 h-6 text-red-600" />
              }
            </div>
          </div>
        </div>
      </div>

      {/* Demographics Cards - These update every 30 seconds */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
        {/* Age Groups Card */}
        {/* <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-600">Age Groups</h3>
              <p className="text-xs text-slate-400">Distribution by age range</p>
            </div>
            <div className="bg-indigo-100 p-2 rounded-lg">
              <UsersIcon className="w-4 h-4 text-indigo-600" />
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(demographics.ageGroups).map(([ageGroup, count]) => (
              <div key={ageGroup}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{ageGroup} years</span>
                  <span className="font-semibold text-slate-800">{count}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-1000 bg-gradient-to-r from-indigo-500 to-purple-500"
                    style={{ 
                      width: `${maxAgeValue > 0 ? (count / maxAgeValue) * 100 : 0}%` 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
            <p className="text-xs text-slate-400">
              Based on {demographicTotal} total persons
            </p>
          </div>
        </div> */}

        {/* Gender Distribution Card - Now using stable demographicTotal */}
        {/* <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-600">Gender Distribution</h3>
              <p className="text-xs text-slate-400">Male vs Female count</p>
            </div>
            <div className="bg-pink-100 p-2 rounded-lg">
              <UserCheck className="w-4 h-4 text-pink-600" />
            </div>
          </div>
          <div className="space-y-3">
            {/* Male - Now using demographicTotal for stable percentages */}
            {/* <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  <span className="text-slate-600">Male</span>
                </span>
                <span className="font-semibold text-slate-800">
                  {demographics.gender.male}
                  <span className="text-xs text-slate-400 ml-1">
                    ({demographicTotal > 0 ? Math.round((demographics.gender.male / demographicTotal) * 100) : 0}%)
                  </span>
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-1000 bg-gradient-to-r from-blue-500 to-blue-400"
                  style={{ 
                    width: `${demographicTotal > 0 ? (demographics.gender.male / demographicTotal) * 100 : 0}%` 
                  }}
                />
              </div>
            </div> */}

            {/* Female - Now using demographicTotal for stable percentages */}
            {/* <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-pink-500"></span>
                  <span className="text-slate-600">Female</span>
                </span>
                <span className="font-semibold text-slate-800">
                  {demographics.gender.female}
                  <span className="text-xs text-slate-400 ml-1">
                    ({demographicTotal > 0 ? Math.round((demographics.gender.female / demographicTotal) * 100) : 0}%)
                  </span>
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-1000 bg-gradient-to-r from-pink-500 to-pink-400"
                  style={{ 
                    width: `${demographicTotal > 0 ? (demographics.gender.female / demographicTotal) * 100 : 0}%` 
                  }}
                />
              </div>
            </div> */}

            {/* Summary - Now using demographicTotal */}
            {/* <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Total: {demographicTotal}</span>
                <span>Male: {demographics.gender.male}</span>
                <span>Female: {demographics.gender.female}</span>
                <span>Ratio: 
                  {demographics.gender.female > 0 
                    ? `${Math.round(demographics.gender.male / demographics.gender.female * 10) / 10}:1` 
                    : 'N/A'}
                </span>
              </div>
            </div> */}
          {/* </div> */}
          {/* <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
            <p className="text-xs text-slate-400">
              Based on {demographicTotal} total persons
            </p>
          </div> */}
        {/* </div> */}
      {/* </div> */}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Camera Grid/List View */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        {/* Header with controls */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Camera Feed Monitor
            </h2>
            {lastUpdate && (
              <p className="text-sm text-slate-500 mt-1">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "grid" 
                  ? "bg-blue-100 text-blue-600" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              title="Grid View"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "list" 
                  ? "bg-blue-100 text-blue-600" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-2 rounded-lg transition-colors ${
                isRefreshing 
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              title="Refresh Connection"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Grid View */}
        {viewMode === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {cameras.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Camera className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>{isRefreshing ? 'Reconnecting...' : 'Waiting for camera data...'}</p>
              </div>
            ) : (
              cameras.map(([cameraName, numPersons]) => (
                <div
                  key={cameraName}
                  className="bg-slate-50 rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getDotColor(numPersons)}`} />
                      <span className="font-medium text-slate-800 text-sm truncate">
                        {cameraName}
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(numPersons)}`}>
                      {getStatusLabel(numPersons)}
                    </span>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {numPersons}
                      </p>
                      <p className="text-xs text-slate-500">persons detected</p>
                    </div>
                    <div className={`p-2 rounded-lg ${numPersons > 0 ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Users className={`w-4 h-4 ${numPersons > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* List View */}
        {viewMode === "list" && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">#</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Camera Name</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Persons</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Activity</th>
                </tr>
              </thead>
              <tbody>
                {cameras.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-slate-500">
                      {isRefreshing ? 'Reconnecting...' : 'Waiting for camera data...'}
                    </td>
                  </tr>
                ) : (
                  cameras.map(([cameraName, numPersons], index) => (
                    <tr key={cameraName} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-slate-500">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-700">{cameraName}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(numPersons)}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${getDotColor(numPersons)}`} />
                          {getStatusLabel(numPersons)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-slate-800">{numPersons}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-300 ${
                              numPersons > 0 ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            style={{ width: `${Math.min((numPersons / 10) * 100, 100)}` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebSocketDataFeed;